import React, { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  Button,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from '@material-ui/core';
import { CircleCheckIcon, CircleTimes, DownloadIcon, EditIcon, SyncIcon, TrashIcon } from 'components/Icons';

import {
  assembleiasAssessorLoadEvento,
  assembleiaAssessorActionsRemoveDocument,
  documentoActionDownload,
  assembleiaAssessorActionsUpdateDocument,
} from 'store/modules/AssembleiasAssessorActions';

import ErrorHandler from 'utils/ErrorHandler';
import TextRange from 'utils/TextRange';
import trigger from 'utils/DownloadTrigger';
import useStyle from './style';
import { DocumentoResponse } from 'api/Assessor/documentos';
import TipoDocumento from 'models/TipoDocumento';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { getUniqueKey } from 'utils/KeyUtils';
import ConfirmModalDocumentos, { IConfirmModalGenericProps } from 'components/ConfirmModal/ConfirmModalDocumentos';

interface Props extends DocumentoResponse {
  idEvento: number;
  categorias?: TipoDocumento[];
  atualizarDocumentos: Function;
}

const MAXLENGHT = 250;

export default function Documento({
  codigo,
  descricao,
  idDocumento,
  nome,
  flgVisivelAssociado,
  tipoDocumento,
  idEvento,
  categorias,
  atualizarDocumentos
}: Props) {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const [isEditing, setEditing] = useState<boolean>(false);
  const [isUpdating, setUpdating] = useState<boolean>(false);
  const [isDownloading, setDownloading] = useState<boolean>(false);
  const [isRemoving, setRemoving] = useState<boolean>(false);
  const [ idTipoDocumento, setIdTipoDocumento ] = useState<number>(tipoDocumento.id);
  const [ flgVisivel, setFlgVisivel ] = useState<boolean>(flgVisivelAssociado);
  const formEditId = useMemo(() => `edit_desc_${idDocumento}`, [idDocumento]);
  
  const resetDesc = useCallback(() => {
    const form = document.getElementById(formEditId) as HTMLFormElement;
    const textarea = form.elements.item(0) as HTMLTextAreaElement;
    textarea.value = descricao;
    setEditing(false);
  }, [descricao, formEditId]);

   const handleDescEdit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const textarea = event.currentTarget.elements.namedItem('desDocumento') as HTMLTextAreaElement;
      const descricao = textarea.value;
      setUpdating(true);
      dispatch(
        assembleiaAssessorActionsUpdateDocument(
          {
            descricao,
            idTipoDocumento,
            flgVisivelAssociado: flgVisivel,
            
          },
          idDocumento,
          () => {
            dispatch(
              assembleiasAssessorLoadEvento(idEvento, () => {
                setUpdating(false);
                setEditing(false);
                atualizarDocumentos();
              })
            );
          },
          error => {
            ErrorHandler(error);
            setUpdating(false);
          }
        )
      );
  },[dispatch, idEvento, idDocumento, flgVisivel, idTipoDocumento, atualizarDocumentos]);

  const downloadDocumento = useCallback(() => {
    setDownloading(true);
    dispatch(
      documentoActionDownload(
        codigo,
        (arrayBuffer: ArrayBuffer) => {
          setDownloading(false);
          trigger(nome, arrayBuffer);
        },
        () => {
          setDownloading(false);
        }
      )
    );
  }, [dispatch, codigo, nome]);

  const removeDocumento = useCallback(() => {
    setRemoving(true);
    dispatch(
      assembleiaAssessorActionsRemoveDocument(
        idDocumento,
        () => {
          dispatch(assembleiasAssessorLoadEvento(idEvento));
          atualizarDocumentos()
        },
        error => {
          setRemoving(false);
          ErrorHandler(error);
        }
      )
    );
  }, [dispatch, idDocumento, idEvento, atualizarDocumentos]);

  const modalConfirmar = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatch(
      modalsActionsOpen(
        ConfirmModalDocumentos,
        {
          genericProps: {
            contentText: (
              <span
                dangerouslySetInnerHTML={{
                  __html: t('EditarDocumentosModal.text', {
                    assembleia: '<strong>' + descricao + '</strong>',
                  }),
                }}
              />
            ),
            confirmLabel: t('EditarDocumentosModal.buttonConfirm'),
            key: modalKey,
            confirmCallback: removeDocumento,
          } as IConfirmModalGenericProps,
        },
        modalKey
      )
    );
  },[t, dispatch, descricao, removeDocumento]);


  const handleFlag = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setUpdating(true);
      dispatch(
        assembleiaAssessorActionsUpdateDocument(
          {
            descricao,
            idTipoDocumento,
            flgVisivelAssociado: event.target.checked
          },
          idDocumento,
          () => {
            dispatch(
              assembleiasAssessorLoadEvento(idEvento, () => {
                setUpdating(false);
                setEditing(false);
                setFlgVisivel(!flgVisivel)
                atualizarDocumentos();
              })
            );
          },
          error => {
            ErrorHandler(error);
            setUpdating(false);
            setEditing(false);
          }
        )
      );
  },[dispatch, idEvento, idDocumento, descricao, flgVisivel, idTipoDocumento, atualizarDocumentos]);

  return (
    <Grid container className={isRemoving ? classes.documentRemoving : undefined}>
      <Grid item xs={10} className={classes.documentName}>
        <Typography>
          <strong>{nome}</strong>
        </Typography>
      </Grid>
      <Grid item xs={10} className={classes.documentName}>
        {
          !isEditing ?
            <Typography >{tipoDocumento.descricao}</Typography> : 
          <TextField
            label={t('EditarDocumentosModal.labelInputCategoria')}
            SelectProps={{
              value:idTipoDocumento || '',
              onChange: evt => setIdTipoDocumento(evt.target.value as number),
            }}
            inputProps={{
              readOnly: isUpdating || isRemoving
            }}
            select
            fullWidth
          >
            {
              categorias!.map((categoria: TipoDocumento) => {
                if(!categoria.flgDeleted){
                  return (
                    <MenuItem key={categoria.id} value={categoria.id} selected={idTipoDocumento === categoria.id} >
                      {categoria.descricao}
                    </MenuItem>
                  )
                } return undefined;
              })
            }
          </TextField>
        }
      </Grid>
      <Grid item xs={2} className={classes.documentButtonRemove}>
        <IconButton color="primary" onClick={() => modalConfirmar()} disabled={isUpdating || isEditing || isRemoving}>
          {isRemoving ? <SyncIcon animated /> : <TrashIcon size={24} />}
        </IconButton>
      </Grid>
      <Grid item xs={10} className={ (isEditing || isUpdating || isRemoving) ? classes.documentButtonOcultoDownload : classes.documentButtonDownload}>
        <Button
          variant="text"
          color="primary"
          endIcon={isDownloading ? <SyncIcon animated /> : <DownloadIcon />}
          onClick={downloadDocumento}
        >
          {t('EditarDocumentosModal.buttonDownload')}
        </Button>
      </Grid>
      { (isEditing || (!isEditing && descricao !== null)) &&
        <Grid item xs={10} className={classes.documentDescription}>
          { !isEditing ?
              <Typography >{descricao} </Typography> : 
            (
              <form id={formEditId} onSubmit={handleDescEdit}>
                <TextField
                  name="desDocumento"
                  variant="outlined"
                  defaultValue={descricao}
                  disabled={isUpdating || isRemoving}
                  placeholder={
                    !descricao?.length
                      ? t(`EditarDocumentosModal.noDescription.${isEditing ? 'editing' : 'disabled'}`)
                      : undefined
                  }
                  onChange={e => {
                    e.currentTarget.value = TextRange(e.currentTarget.value.replace(/(\r?\n|\r){2}/g, '\n'), MAXLENGHT);
                  }}
                  rowsMax={3}
                  multiline
                  fullWidth
                />
              </form>
            )
          }
        </Grid>
      }
      <Grid item xs={2} className={classes.documentButtonEdit}>
        <IconButton onClick={() => setEditing(true)} disabled={isEditing || isRemoving || isUpdating}>
          {isUpdating ? <SyncIcon animated /> : <EditIcon />}
        </IconButton>
      </Grid>
      {isEditing && (
        <Grid item xs={10} className={classes.documentFormActions}>
          <Button variant="text" startIcon={<CircleTimes />} onClick={resetDesc} disabled={isUpdating}>
            {t('EditarDocumentosModal.buttonCancel')}
          </Button>
          <Button
            variant="text"
            type="submit"
            form={formEditId}
            startIcon={<CircleCheckIcon />}
            disabled={isUpdating}
          >
            {t('EditarDocumentosModal.buttonSave')}
          </Button>
        </Grid>
      )}
      <Grid item xs={12} className={classes.documentCheckboxRow}>
        <FormControlLabel
          label={t('EditarDocumentosModal.labelAvailable')}
          control={<Checkbox onChange={handleFlag} color="primary" />}
          checked={flgVisivel}
          labelPlacement="start"
          disabled={isUpdating || isRemoving || isEditing}
          className={classes.documentCheckboxLabel}
        />
        <Divider />
      </Grid>
    </Grid>
  );
}
